import React from 'react';
import { AbstractButton, Box, Divider, Flex, Icon, Img, StaticChip, Text, Tooltip } from 'pounceV2';
import { Link as RRLink } from 'react-router-dom';
import urls from 'Source/urls';

import { AlertStatus, Permission } from 'Generated/schema';
import useAIEnabled from 'Hooks/useAIEnabled';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import RoleRestrictedAccess from 'Components/utils/RoleRestrictedAccess';
import useEventHandler from 'Hooks/useEventHandler';
import storage from 'Helpers/storage';
import { useAppConfigSafe } from 'Components/utils/AppBootstrapper/AppConfigContext';
import { useTheme } from '@mui/material';
import logoWordmarkDark from 'Assets/panther-plain-logo.svg';
import logoWordmarkLight from 'Assets/panther-plain-logo-light.svg';
import logoShieldDark from 'Assets/panther-minimal-logo.svg';
import logoShieldLight from 'Assets/panther-minimal-logo-light.svg';
import {
  COLLAPSED_SIDE_NAVIGATION_WIDTH,
  EXPANDED_SIDE_NAVIGATION_WIDTH,
  NAV_ICON_SIZE,
  NAV_ROW_SIZE,
  railCenterOffset,
  SIDE_NAV_EXPANDED_STORAGE_KEY,
  Z_INDEX_SIDE_NAV,
} from '../constants';
import { NavSectionItem } from '../types';
import SideNavLinkV3 from './SideNavLink/SideNavLinkV3';
import ProfileInfoV3 from './ProfileInfoV3';
import useDashboardAllowedPermissions from './useDashboardAllowedPermissions';

// Runbooks is designed but not yet shipped; keep the nav entry off until the feature lands.
const ENABLE_RUNBOOKS = false;

type SideNavigationProps = {
  isNavExpanded: boolean;
  setIsNavExpanded: React.Dispatch<React.SetStateAction<boolean>>;
};

const SideNavigationV3 = ({ isNavExpanded, setIsNavExpanded }: SideNavigationProps) => {
  const theme = useTheme();
  const navColors = theme.palette.componentTokens.navigation.colors;

  const handleNavToggle = useEventHandler(() => {
    storage.local.write(SIDE_NAV_EXPANDED_STORAGE_KEY, !isNavExpanded);
    setIsNavExpanded(!isNavExpanded);

    // Manually trigger a resize event in case any components have resize handlers
    // (like the TimeSeriesChart component)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 250);
  });

  const dashboardAllowedPermissions = useDashboardAllowedPermissions();
  const isAIEnabled = useAIEnabled();
  const enableChatHistory = useFeatureToggle('ENABLE_CHAT_HISTORY');
  const enableContentCatalog = useFeatureToggle('ENABLE_CONTENT_CATALOG');
  const aiEnableScheduledPrompts = useAppConfigSafe()?.aiEnableScheduledPrompts ?? false;

  const logoWordmark = theme.palette.mode === 'light' ? logoWordmarkLight : logoWordmarkDark;
  const logoShield = theme.palette.mode === 'light' ? logoShieldLight : logoShieldDark;

  // Sidebar rows as data: `permissions` -> RoleRestrictedAccess, `enabled` -> feature flag.
  const navSections: { id: string; items: NavSectionItem[] }[] = [
    {
      id: 'ai',
      items: [
        {
          icon: 'sparkle-fill',
          to: urls.ai.home(),
          exact: true,
          label: 'Panther AI',
          enabled: isAIEnabled,
        },
        {
          icon: 'history',
          to: urls.ai.history.home(),
          label: 'Chat History',
          enabled: isAIEnabled && enableChatHistory,
          permissions: [Permission.RunPantherAi],
        },
        {
          icon: 'paper-plane',
          to: urls.ai.scheduledPrompts.list(),
          label: 'Scheduled Prompts',
          enabled: isAIEnabled && aiEnableScheduledPrompts,
          permissions: [Permission.DataAnalyticsRead, Permission.RunPantherAi],
        },
        {
          icon: 'health',
          to: urls.dashboard.home(),
          label: 'Dashboards',
          permissions: dashboardAllowedPermissions,
        },
      ],
    },
    {
      id: 'investigate',
      items: [
        {
          icon: 'alert-circle-filled',
          to: urls.alerts.list({
            disableDefaultParams: false,
            statuses: [AlertStatus.Open, AlertStatus.Triaged],
          }),
          label: 'Alerts',
          permissions: [Permission.AlertRead, Permission.AlertModify],
        },
        {
          icon: 'search',
          to: urls.investigate.search(),
          // Search History and Data Explorer are tabs of the Search page, so the
          // Search item stays active on their routes too.
          activePaths: [urls.investigate.searchHistory(), urls.investigate.dataExplorer()],
          label: 'Search',
          permissions: [Permission.DataAnalyticsRead],
        },
        {
          icon: 'save',
          to: urls.investigate.savedSearches.list(),
          label: 'Saved Searches',
          permissions: [Permission.DataAnalyticsRead],
        },
        {
          icon: 'resource',
          to: urls.investigate.cloudResources.list(),
          label: 'Cloud Resources',
          permissions: [Permission.ResourceRead, Permission.ResourceModify],
        },
        {
          icon: 'catalog',
          to: urls.contentCatalog.home(),
          label: 'Content Catalog',
          enabled: enableContentCatalog,
          permissions: [
            Permission.RuleRead,
            Permission.PolicyRead,
            Permission.LogSourceRead,
            Permission.DataAnalyticsRead,
          ],
          trailing: (
            <StaticChip type="outline" variant="success" size="sm">
              New
            </StaticChip>
          ),
        },
        {
          icon: 'detection',
          to: urls.build.home(),
          label: 'Detections',
          permissions: [
            Permission.PolicyRead,
            Permission.PolicyModify,
            Permission.RuleRead,
            Permission.RuleModify,
          ],
        },
        {
          icon: 'enrichment-sources',
          to: urls.configure.enrichmentProviders.home(),
          label: 'Enrichments',
          permissions: [Permission.LookupRead, Permission.LookupModify],
        },
        {
          icon: 'schemas',
          to: urls.configure.schemas.list({ disableDefaultParams: false }),
          label: 'Schemas',
          permissions: [Permission.LogSourceRead, Permission.LogSourceModify],
        },
        {
          icon: 'play-all',
          to: urls.runbooks.home(),
          label: 'Runbooks',
          enabled: ENABLE_RUNBOOKS,
        },
      ],
    },
    {
      id: 'data',
      items: [
        {
          icon: 'log-source',
          to: urls.configure.logSources.list({ disableDefaultParams: false }),
          label: 'Log Sources',
          permissions: [Permission.LogSourceRead, Permission.LogSourceModify],
        },
        {
          icon: 'cloud-security',
          to: urls.configure.cloudAccounts.list(),
          label: 'Cloud Accounts',
          permissions: [Permission.CloudsecSourceRead, Permission.CloudsecSourceModify],
        },
        {
          icon: 'output',
          to: urls.configure.alertDestinations.list(),
          label: 'Alert Destinations',
          permissions: [Permission.DestinationRead, Permission.DestinationModify],
        },
      ],
    },
  ];

  return (
    <Flex
      as="aside"
      flexDirection="column"
      py={4}
      position="absolute"
      zIndex={Z_INDEX_SIDE_NAV}
      width={isNavExpanded ? EXPANDED_SIDE_NAVIGATION_WIDTH : COLLAPSED_SIDE_NAVIGATION_WIDTH}
      height="100%"
      sx={{
        borderRight: `1px solid ${navColors.stroke.containerBase}`,
        backdropFilter: 'blur(50px)',
      }}
      backgroundColor={navColors.background.containerBase}
      transition="width 250ms ease"
      data-dd-privacy="allow"
    >
      <Box
        as={RRLink}
        to="/"
        display="flex"
        alignItems="center"
        justifyContent={isNavExpanded ? 'flex-start' : 'center'}
        px={isNavExpanded ? 5 : 0}
        mb={4}
        height={32}
        flexShrink={0}
        data-dd-privacy="allow"
      >
        <Img src={isNavExpanded ? logoWordmark : logoShield} alt="Panther home" height={28} />
      </Box>

      <Box
        as="nav"
        aria-label="Main"
        width="100%"
        flex="1 1 auto"
        overflowY="auto"
        overflowX="hidden"
        style={{ scrollbarGutter: 'stable' }}
      >
        <Flex direction="column" as="ul" pr={1}>
          {navSections.map((section, sectionIndex) => {
            const visibleItems = section.items.filter(item => item.enabled !== false);
            return (
              <React.Fragment key={section.id}>
                {sectionIndex > 0 && (
                  <Divider color={navColors.stroke.containerBase} my={3} mx={3} />
                )}
                {visibleItems.map(item => {
                  const row = (
                    <Box as="li" key={item.label} mb="2px" ml={isNavExpanded ? 2 : 0}>
                      <SideNavLinkV3
                        icon={item.icon}
                        to={item.to}
                        label={item.label}
                        exact={item.exact}
                        activePaths={item.activePaths}
                        trailing={item.trailing}
                        isNavExpanded={isNavExpanded}
                      />
                    </Box>
                  );
                  return item.permissions ? (
                    <RoleRestrictedAccess key={item.label} allowedPermissions={item.permissions}>
                      {row}
                    </RoleRestrictedAccess>
                  ) : (
                    row
                  );
                })}
              </React.Fragment>
            );
          })}
        </Flex>
      </Box>

      <Box as="footer" flexShrink={0} width="100%" pr={1}>
        <Divider color={navColors.stroke.containerBase} my={3} mx={3} />
        <Box mb="2px" ml={isNavExpanded ? 2 : 0}>
          <SideNavLinkV3
            icon="settings-alt"
            to={urls.settings.home()}
            label="Settings"
            isNavExpanded={isNavExpanded}
          />
        </Box>
        <Box ml={isNavExpanded ? 2 : 0}>
          <ProfileInfoV3 isNavExpanded={isNavExpanded} />
        </Box>
        <Divider color={navColors.stroke.containerBase} my={3} mx={3} />
        {/* Styled like SideNavLinkV3 rows so the hover pill matches the menu items */}
        <Box ml={isNavExpanded ? 2 : 0}>
          <Tooltip content={!isNavExpanded ? 'Expand sidebar' : undefined}>
            <AbstractButton
              aria-label="Toggle navigation menu"
              onClick={handleNavToggle}
              display="block"
              width="100%"
            >
              <Flex
                as="span"
                alignItems="center"
                justifyContent={isNavExpanded ? 'flex-start' : 'center'}
                // Collapsed: offset the row to the rail center so it aligns with the icons above.
                ml={isNavExpanded ? '7px' : railCenterOffset(NAV_ROW_SIZE)}
                mr={isNavExpanded ? '7px' : 0}
                px={isNavExpanded ? 3 : 2}
                py={2}
                borderRadius="large"
                height={NAV_ROW_SIZE}
                width={isNavExpanded ? 'auto' : NAV_ROW_SIZE}
                color={navColors.text.resting}
                transition="background-color 250ms ease"
                _hover={{
                  backgroundColor: navColors.backgroundRowItems.hover,
                  color: navColors.text.hover,
                }}
              >
                <Icon
                  type="chevron-left"
                  size={NAV_ICON_SIZE}
                  transition="transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 80ms"
                  transform={isNavExpanded ? 'rotate(0deg)' : 'rotate(180deg)'}
                />
                {isNavExpanded && (
                  <Text
                    as="span"
                    variant="body/md"
                    fontWeight="medium"
                    ml={3}
                    color={navColors.text.resting}
                  >
                    Collapse
                  </Text>
                )}
              </Flex>
            </AbstractButton>
          </Tooltip>
        </Box>
      </Box>
    </Flex>
  );
};

export default React.memo(SideNavigationV3);
